import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PlayCircle, CheckCircle2, ChevronLeft, BookOpen, 
  Download, Bookmark,  StickyNote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { COURSES, getEnrolledCourses, updateCourseProgress } from '@/lib/data';

export default function CoursePlayer() {
  const { id } = useParams();
  const course = COURSES.find(c => c.id === id);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(
    () => getEnrolledCourses().find(e => e.courseId === id)?.completedLessons || []
  );
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [bookmarked, setBookmarked] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (!course) return <div className="pt-20 text-center">Course not found</div>;

  const allLessons = course.curriculum.flatMap((mod, mi) =>
    Array.from({ length: mod.lessons }, (_, li) => ({
      moduleIndex: mi,
      moduleName: mod.module,
      lessonIndex: mi * 100 + li,
      title: `${mod.module} - Lesson ${li + 1}`,
    }))
  );

  const totalLessons = allLessons.length;
  const progress = Math.round((completedLessons.length / totalLessons) * 100);
  const currentLessonData = allLessons[currentLesson];

  const markComplete = () => {
    const lessonIdx = currentLessonData.lessonIndex;
    if (!completedLessons.includes(lessonIdx)) {
      const updated = [...completedLessons, lessonIdx];
      setCompletedLessons(updated);
      updateCourseProgress(id, lessonIdx);
    }
    if (currentLesson < totalLessons - 1) {
      setCurrentLesson(currentLesson + 1);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-background">
      {/* Top Bar */}
      <div className="bg-card border-b px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon"><ChevronLeft className="w-5 h-5" /></Button>
          </Link>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-sm truncate">{course.title}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Progress value={progress} className="w-24 h-1.5" />
              <span className="text-xs text-muted-foreground">{progress}%</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setShowNotes(!showNotes)}>
            <StickyNote className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <BookOpen className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Video Area */}
        <div className="flex-1 min-w-0">
          {/* Video Player Mock */}
          <div className="aspect-video bg-foreground/95 flex items-center justify-center relative">
            <div className="text-center">
              <PlayCircle className="w-16 h-16 text-white/40 mx-auto mb-3" />
              <p className="text-white/60 text-sm">{currentLessonData?.title}</p>
              <p className="text-white/30 text-xs mt-1">Video Player Mock</p>
            </div>
            {/* Playback bar */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <div className="h-1 bg-white/20 rounded-full">
                <div className="h-1 bg-primary rounded-full w-1/3" />
              </div>
            </div>
          </div>

          {/* Lesson Info */}
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">{currentLessonData?.title}</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setBookmarked(prev =>
                    prev.includes(currentLesson) ? prev.filter(b => b !== currentLesson) : [...prev, currentLesson]
                  )}
                >
                  <Bookmark className={`w-4 h-4 ${bookmarked.includes(currentLesson) ? 'fill-primary text-primary' : ''}`} />
                  Bookmark
                </Button>
                <Button variant="outline" size="sm" className="gap-1">
                  <Download className="w-4 h-4" /> Resources
                </Button>
              </div>
            </div>

            <Button onClick={markComplete} className="gap-2">
              <CheckCircle2 className="w-4 h-4" />
              {completedLessons.includes(currentLessonData?.lessonIndex) ? 'Completed ✓' : 'Mark as Complete & Next'}
            </Button>

            {/* Notes */}
            {showNotes && (
              <div className="bg-card rounded-lg border p-4 space-y-3">
                <h3 className="font-heading font-semibold text-sm">My Notes</h3>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Write your notes for this lesson..."
                  className="min-h-[100px]"
                />
              </div>
            )}
          </div>
        </div>

        {/* Lesson Sidebar */}
        {sidebarOpen && (
          <div className="hidden lg:block w-80 border-l bg-card overflow-y-auto max-h-[calc(100vh-57px)] sticky top-[57px]">
            <div className="p-4 border-b">
              <h3 className="font-heading font-semibold text-sm">Course Content</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{completedLessons.length}/{totalLessons} lessons completed</p>
            </div>
            <div className="divide-y">
              {allLessons.map((lesson, i) => {
                const isCompleted = completedLessons.includes(lesson.lessonIndex);
                const isCurrent = i === currentLesson;
                return (
                  <button
                    key={i}
                    onClick={() => setCurrentLesson(i)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-muted/50 ${
                      isCurrent ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : isCurrent ? (
                      <PlayCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 flex-shrink-0" />
                    )}
                    <span className={`text-xs truncate ${isCurrent ? 'font-medium text-primary' : 'text-muted-foreground'}`}>
                      {lesson.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}